import { useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  deleteUser,
  EmailAuthProvider,
  GoogleAuthProvider,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithRedirect,
  signOut,
  type User,
} from "firebase/auth";
import { deleteDoc, doc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../lib/firebase";

const leaveCouple = httpsCallable(functions, "leaveCouple");

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setInitializing(false);
    });
    return unsub;
  }, []);

  return {
    user,
    initializing,
    login: (email: string, password: string) => signInWithEmailAndPassword(auth, email, password),
    register: (email: string, password: string) => createUserWithEmailAndPassword(auth, email, password),
    // Redirect rather than a popup: popups are unreliable in installed/
    // standalone PWAs on iOS (the context this app is explicitly built for),
    // where they're often silently blocked. onAuthStateChanged above picks
    // up the signed-in user automatically once Google redirects back.
    loginWithGoogle: () => signInWithRedirect(auth, new GoogleAuthProvider()),
    logout: () => signOut(auth),
    resetPassword: (email: string) => sendPasswordResetEmail(auth, email),
    deleteAccount: async (password: string) => {
      const current = auth.currentUser;
      if (!current || !current.email) throw new Error("Nicht angemeldet.");
      const credential = EmailAuthProvider.credential(current.email, password);
      await reauthenticateWithCredential(current, credential);
      // Must run before deleteUser() below — it needs request.auth, which is
      // gone the instant the Auth account is deleted. Leaves the partner's
      // shared content (calendar, lists, notes, ...) completely untouched,
      // just removes this uid from memberUids so they're no longer stuck
      // "connected" to an account that no longer exists.
      await leaveCouple().catch(() => {});
      // The username reservation is intentionally permanent (see firestore.rules)
      // and outlives the account, but the profile doc itself should go — leaving
      // it behind would make it a readable, un-owned orphan.
      await deleteDoc(doc(db, "users", current.uid)).catch(() => {});
      await deleteUser(current);
    },
  };
}
